import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { formatAddress, formatNumber } from '../utils/helpers';
import { NAV_ITEMS } from '../config/constants';
import { isAdminAddress } from '../config/admin';
import './Layout.css';

const NavIcon = ({ type, isActive }) => {
  const icons = {
    home: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
        <polyline points="9,22 9,12 15,12 15,22"/>
      </svg>
    ),
    market: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
        <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
      </svg>
    ),
    nft: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21,15 16,10 5,21"/>
      </svg>
    ),
    team: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 00-3-3.87"/>
        <path d="M16 3.13a4 4 0 010 7.75"/>
      </svg>
    )
  };
  return <span className={`nav-icon-svg ${isActive ? 'active-icon' : ''}`}>{icons[type]}</span>;
};

const Layout = ({ children }) => {
  const location = useLocation();
  const { account, usdtBalance, isConnecting, connectWallet, disconnectWallet } = useWeb3();
  const showAdminLink = isAdminAddress(account);

  return (
    <div className="layout">
      {/* Background with particles */}
      <div className="bg-universe">
        <div className="bg-grid"></div>
        <div className="bg-orb bg-orb-1"></div>
        <div className="bg-orb bg-orb-2"></div>
        <div className="bg-orb bg-orb-3"></div>
        <div className="scan-line"></div>
        {/* Floating Particles */}
        <div className="particles-container">
          {[...Array(30)].map((_, i) => (
            <span key={i} className="bg-particle" style={{ 
              '--delay': `${Math.random() * 10}s`,
              '--x': `${Math.random() * 100}%`,
              '--duration': `${15 + Math.random() * 15}s`
            }}></span>
          ))}
        </div>
      </div>

      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <div className="logo-icon">
              <img src="/logo.gif" alt="Adrenaline" />
              <div className="logo-ring"></div>
            </div>
            <div className="logo-text">
              <span className="logo-title">ADRENALINE NFT</span>
              <span className="logo-sub">NFT</span>
            </div>
          </div>
          
          <div className="header-actions">
            {showAdminLink && (
              <NavLink to="/admin" className="admin-link" title="Admin Panel">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <span>Admin</span>
              </NavLink>
            )}
            {account ? (
              <button className="wallet-btn" onClick={disconnectWallet}>
                <div className="wallet-balance">
                  <span className="balance-value font-numbers">{formatNumber(parseFloat(usdtBalance))}</span>
                  <span className="balance-symbol">USDT</span>
                </div>
                <div className="wallet-address">
                  <span className="address-dot"></span>
                  <span>{formatAddress(account)}</span>
                </div>
              </button>
            ) : (
              <button className="btn btn-primary connect-btn" onClick={connectWallet} disabled={isConnecting}>
                {isConnecting ? 'Connecting...' : 'Connect'}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <div className="nav-bg"></div>
        <div className="nav-items">
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
              >
                <div className={`nav-icon-wrapper ${isActive ? 'active' : ''}`}>
                  <NavIcon type={item.icon} isActive={isActive} />
                  {isActive && <div className="icon-pulse"></div>}
                </div>
                <span className="nav-label">{item.label}</span>
                {isActive && <span className="nav-indicator"></span>}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
